/**
 * The `@auger/core` domain, over the app's Apollo client.
 *
 * The domain takes a `query`/`mutate` pair and owns field semantics from there;
 * Apollo keeps owning the cache and reactivity. Nothing about reads changes —
 * `useQuery` and the normalized cache are untouched.
 *
 * Reads made on behalf of a WRITE go `network-only` on purpose. The planner diffs
 * a patch against the record's current relation rows, so a cache-first read could
 * plan against a stale set and reconcile to the wrong thing — deleting rows that
 * were added elsewhere, or re-adding ones already removed. Correctness beats a
 * saved round trip on the write path specifically.
 *
 * Writes to link rows fan out a refetch of the queries that read them (see
 * LINK_QUERIES below), so a `useQuery` on any of them stays in step with no
 * cache surgery and no caller has to remember to ask.
 */

import { type Auger, createAuger } from "@auger/core"
import { asAdmin } from "@/common/authorization"
import { client as defaultClient } from "@/init/apollo"
import { getUserById } from "@/stores/users"

type ApolloLike = {
  query(opts: {
    query: unknown
    variables?: unknown
    fetchPolicy?: string
    context?: unknown
  }): Promise<{ data: unknown }>
  mutate(opts: { mutation: unknown; variables?: unknown; context?: unknown }): Promise<{
    data: unknown
  }>
  refetchQueries?(opts: { include: string[] }): Promise<unknown>
}

// The queries that read record_links rows, and core's mutations that write them.
// A link write refetches every active observer of these: a record's backlinks
// and the record itself (by slug or id) carry its links.
const LINK_QUERIES = ["fetchRecordBacklinks", "fetchRecordBySlug", "fetchRecordById"]
const LINK_MUTATIONS = new Set(["insertManyRecordLinks", "deleteLinksByIds"])

// A list's rows: thousands of full records with their relations, read whole and
// held by the caller (useEntries). Normalizing that into the Apollo cache
// dominates GC on big lists and nothing reads it back, so they skip the cache.
const UNCACHED_QUERIES = new Set([
  "fetchListContents",
  "fetchListMemberEntries",
  "fetchListMatchEntries",
  "fetchFilterPreviewEntries",
])

const operationName = (op: unknown): string | undefined =>
  (op as { definitions?: Array<{ kind: string; name?: { value: string } }> }).definitions?.find(
    (d) => d.kind === "OperationDefinition"
  )?.name?.value

/** Refetch the link-reading queries after a link write. Fire-and-forget: the
 *  write already landed, so a failed refetch is logged, not thrown. */
function refetchAfter(apollo: ApolloLike, op: unknown) {
  if (!LINK_MUTATIONS.has(operationName(op) ?? "")) return
  apollo.refetchQueries?.({ include: LINK_QUERIES })?.catch((err: unknown) => {
    console.warn("refetch after a link write failed:", err)
  })
}

/** The Hasura role a request runs as. `admin` is for the admin tools whose
 *  database functions (merge_tags, …) have no staff permission at all. The
 *  role name is ADMIN_ROLE (common/authorization.ts). */
export type AugerRole = "admin"

const ready = new Map<AugerRole | "default", WeakMap<object, Auger>>()

/**
 * The domain for a client. Keyed by client OBJECT
 * IDENTITY, not by any notion of "which test" — memoized so the domain is
 * built once per client rather than once per call. A fresh client object
 * (e.g. a new `fakeClient()` per test) gets its own cache and never sees the
 * app's; reusing the SAME client object across multiple test cases shares
 * their cached Auger too, catalog included — isolation is a consequence of
 * constructing a new object, not a property this function enforces itself.
 *
 * `role` runs every request as that Hasura role — a separate domain instance,
 * since the role belongs to the transport, not to core's `query`/`mutate`.
 */
/** `{ context }` for Apollo when there is a role header to send, else nothing. */
function withContext(context: ReturnType<typeof asAdmin> | undefined) {
  return context ? { context } : {}
}

export function getAuger(client: unknown = defaultClient, opts: { role?: AugerRole } = {}): Auger {
  // The ONE structural cast, here instead of `as never` at every caller: the
  // real ApolloClient's `query` wants a DocumentNode and a narrower
  // fetchPolicy union than this seam declares, so it never satisfies
  // ApolloLike nominally. The adapter only ever passes codegen documents and
  // fetchPolicy "network-only" or "no-cache", which every Apollo-shaped client
  // accepts.
  const apollo = client as ApolloLike
  const key = opts.role ?? "default"
  const byClient = ready.get(key) ?? new WeakMap<object, Auger>()
  ready.set(key, byClient)
  const cached = byClient.get(apollo)
  if (cached) return cached

  // Per request, not per instance: the instance is cached, and a refreshed
  // token can change which admin role name it allows.
  const context = () => (opts.role === "admin" ? asAdmin() : undefined)

  // The catalog wires the registries when it loads (init/catalog.ts, at boot).
  // Users are the frontend's own: its user rows carry more than the catalog's
  // (the person record and avatar), so the catalog resolves them from the store.
  const auger = createAuger({
    users: (id) => getUserById(id),
    query: ((op: unknown, variables: unknown) =>
      apollo
        .query({
          query: op,
          variables,
          fetchPolicy: UNCACHED_QUERIES.has(operationName(op) ?? "") ? "no-cache" : "network-only",
          ...withContext(context()),
        })
        .then((r) => r.data)) as Parameters<typeof createAuger>[0]["query"],
    // A write may ask for the admin role itself (core's MutateOptions): the
    // data-model writes and tag and asset deletes have rules for heavybit_admin
    // alone, while every staff session's default role is heavybit_staff.
    mutate: ((op: unknown, variables: unknown, writeOpts?: { role?: "admin" }) =>
      apollo
        .mutate({
          mutation: op,
          variables,
          ...withContext(writeOpts?.role === "admin" ? asAdmin() : context()),
        })
        .then((r) => {
          refetchAfter(apollo, op)
          return r.data
        })) as Parameters<typeof createAuger>[0]["mutate"],
  })

  byClient.set(apollo, auger)
  return auger
}
